import React, { useRef, useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useBasket } from 'msp-integrations'
import {
  Spinner,
  Button,
  Field,
  Grid,
  AlertBox,
  Icons,
  CustomLink
} from '../../../index'
import getGiftCardFields, { messages } from './GiftCardForm.fields'
import * as T from './GiftCardForm.types'

const GiftCardForm = ({
  onRedeem = () => {},
  onRemove = () => {}
}: T.GiftCardFormProps) => {
  const fieldConfig = getGiftCardFields()
  const {
    control,
    errors,
    formState,
    handleSubmit,
    clearErrors,
    setError,
    reset
  } = useForm()
  const basket = useBasket()
  const [currentGiftCert, setCurrentGiftCert] = useState<any>(null)
  const { formatMessage } = useIntl()
  const alertBoxRef = useRef<AlertBox>()

  useEffect(() => {
    setCurrentGiftCert(basket?.giftCertificate)
  }, [basket])

  const onFormSubmit = async (data: T.HandleSubmitProps) => {
    const response = await basket.redeemGiftCertificate(data.activationCode)

    if (response.error) {
      setError('global', {
        type: 'manual',
        message: response.ErrorMsg.replace('{0}', data.activationCode)
      })
      alertBoxRef?.current?.showAlert()
    } else {
      reset({
        activationCode: ''
      })
      onRedeem()
    }
  }

  const onRemoveGiftCert = async () => {
    const giftCertificateCode = currentGiftCert?.c_giftCertInternalCode || ''
    const response = await basket.removeGiftCertificate(giftCertificateCode)

    if (response.error) {
      setError('global', {
        type: 'manual',
        message: response.ErrorMsg.replace('{0}', giftCertificateCode)
      })
      alertBoxRef?.current?.showAlert()
    } else {
      await basket.removePaymentInstrument()
      reset({
        activationCode: ''
      })
      onRemove()
    }
  }

  return (
    <div>
      <AlertBox
        ref={alertBoxRef}
        description={errors?.global?.message || ''}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      />
      <form onSubmit={handleSubmit(onFormSubmit)} id={'gift-card-form'}>
        <Grid wrapperStyles='grid-cols-1 md:grid-cols-2 md:gap-2'>
          <Field
            className={'w-full'}
            control={control}
            config={fieldConfig.activationCode}
            error={errors?.activationCode}
            isDisabled={formState.isSubmitting || currentGiftCert}
          />
          <Button
            className='mt-3 w-full md:w-6/12'
            onClick={() => clearErrors()}
            isDisabled={formState.isSubmitting || currentGiftCert}
            isLoading={formState.isSubmitting}
            isSubmit
            loadingContent={<Spinner size='xl' />}
          >
            {formatMessage(messages.button)}
          </Button>
        </Grid>
        {currentGiftCert && (
          <>
            <p>
              {formatMessage(
                {
                  defaultMessage:
                    'You redeemed an amount of {amount} € with the code {code}',
                  id: 'msp.checkout_payment.giftcard.description'
                },
                {
                  amount: currentGiftCert.amount,
                  code: currentGiftCert.c_giftCertActivationCode
                }
              )}
            </p>
            <CustomLink onClick={onRemoveGiftCert}>Remove</CustomLink>
          </>
        )}
      </form>
    </div>
  )
}

export default GiftCardForm
